import { useApiQuery } from './useApiQuery';
import { useApiMutation } from './useApiMutation';
import { dealCostService } from '@/services/dealCostService';
import { APIError } from '@/utils/api-client';
import type {
  DealCost,
  DealCostInvestment,
  DealCostPage,
  DealCostResolve,
  DealCostUpdate,
  DealCostsSummary,
} from '@/types/deal-costs';
import { queryKeys } from './queryKeys';

export const useDealCosts = (
  fundId: string,
  params?: {
    portfolio_company_id?: string;
    page?: number;
    page_size?: number;
  }
) => {
  return useApiQuery<DealCostPage>(
    queryKeys.dealCosts.list(fundId, params),
    () => dealCostService.list(fundId, params),
    {
      enabled: !!fundId,
    }
  );
};

export const useDealCostsSummary = (fundId: string) => {
  return useApiQuery<DealCostsSummary>(
    queryKeys.dealCosts.summary(fundId),
    () => dealCostService.summary(fundId),
    {
      enabled: !!fundId,
    }
  );
};

/** Only the picker needs these, so a closed dialog passes enabled false. */
export const useDealCostInvestments = (fundId: string, enabled = true) => {
  return useApiQuery<DealCostInvestment[]>(
    queryKeys.dealCosts.investments(fundId),
    () => dealCostService.listInvestments(fundId),
    {
      enabled: enabled && !!fundId,
    }
  );
};

export const useUpdateDealCost = (fundId: string) => {
  return useApiMutation<
    DealCost,
    APIError,
    { costId: string; data: DealCostUpdate }
  >(({ costId, data }) => dealCostService.update(fundId, costId, data), {
    invalidateKeys: [
      queryKeys.dealCosts.list(fundId),
      queryKeys.dealCosts.summary(fundId),
      queryKeys.fundTransactions.lists(fundId),
    ],
    successMessage: 'Deal cost updated successfully',
  });
};

export const useResolveDealCost = (fundId: string) => {
  return useApiMutation<
    DealCost,
    APIError,
    { costId: string; data: DealCostResolve }
  >(({ costId, data }) => dealCostService.resolve(fundId, costId, data), {
    invalidateKeys: [
      queryKeys.dealCosts.list(fundId),
      queryKeys.dealCosts.summary(fundId),
      queryKeys.fundTransactions.lists(fundId),
    ],
    successMessage: 'Deal cost resolved',
  });
};

export const useDeleteDealCost = (fundId: string) => {
  return useApiMutation<void, APIError, string>(
    (costId) => dealCostService.delete(fundId, costId),
    {
      invalidateKeys: [
        queryKeys.dealCosts.list(fundId),
        queryKeys.dealCosts.summary(fundId),
        queryKeys.fundTransactions.lists(fundId),
      ],
      successMessage: 'Deal cost deleted successfully',
    }
  );
};
